import { db, kvGet, kvSet, type Category, type ExpenseCategory, type OptionSet, type Product, type Promotion, type DiningTable, type Zone } from './db'

const BASE = import.meta.env.VITE_API_URL ?? ''

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
  }
}

/** Lỗi mạng khác lỗi máy chủ: một cái thì chờ rồi thử lại, cái kia thì báo người dùng. */
export class OfflineError extends Error {
  constructor() {
    super('Không có mạng')
  }
}

let token: string | null = null

export const setToken = (t: string | null) => {
  token = t
}
export const getToken = () => token

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response
  try {
    res = await fetch(`${BASE}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init?.headers,
      },
    })
  } catch {
    throw new OfflineError()
  }

  if (res.status === 204) return undefined as T

  const text = await res.text()
  const body = text ? (JSON.parse(text) as unknown) : null

  if (!res.ok) {
    const message =
      body && typeof body === 'object' && 'message' in body
        ? String((body as { message: unknown }).message)
        : `Máy chủ trả lỗi ${res.status}`
    throw new ApiError(message, res.status)
  }

  return body as T
}

export const api = {
  get: <T>(path: string) => call<T>(path),
  post: <T>(path: string, body?: unknown) =>
    call<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) }),
  put: <T>(path: string, body?: unknown) =>
    call<T>(path, { method: 'PUT', body: JSON.stringify(body ?? {}) }),
  del: <T>(path: string) => call<T>(path, { method: 'DELETE' }),
}

// ───────────────────────── xác thực ─────────────────────────

export type TableLayoutMode = 'Grid' | 'Floor'

export interface ShopSummary {
  id: string
  name: string
  businessType: 'FnB' | 'Retail'
  vatRatePermille: number
  pitRatePermille: number
  taxFreeThreshold: number
  plan: string
  bankBin: string | null
  bankAccountNo: string | null
  bankAccountName: string | null
  /** 'Grid' = lưới đều 2–3 cột (mặc định) · 'Floor' = tự xếp theo mặt bằng. */
  tableLayoutMode: TableLayoutMode
  gridColumns: number
  address: string | null
  phone: string
  /** In lên bill và ghi vào sổ doanh thu — tờ khai thuế bắt buộc có. */
  taxCode: string | null
  /** Quán có xuất hoá đơn điện tử không. Người bán cũng cần biết, nên nó nằm ở đây. */
  eInvoiceEnabled: boolean
  /** Bán xong tự xuất, không đợi khách xin. */
  eInvoiceAutoIssue: boolean
  /** Phí phục vụ cộng vào hoá đơn, phần nghìn. 50 = 5,0%. 0 là quán không thu. */
  serviceChargePermille: number
  /** true = quán thu tiền khi khách ăn xong ra về; false = thu ngay lúc gọi món. */
  payLater: boolean
  /**
   * Chuỗi băm của mã 6 số dùng để huỷ hoá đơn đã thu tiền. `null` là quán chưa đặt mã.
   *
   * Máy bán giữ chuỗi băm chứ không giữ mã, vì việc kiểm tra phải chạy được lúc mất mạng.
   */
  voidPasscodeHash: string | null
  /** Cỡ ô bàn trên sơ đồ, phần trăm. 100 là cỡ gốc. */
  tableTileScale: number
  /** Gói trả phí dùng tới bao giờ. `null` là quán chưa từng mua gói nào. */
  planExpiresAt: string | null
}

export interface AuthResult {
  token: string
  expiresAt: string
  userId: string
  name: string
  role: string
  shop: ShopSummary
  isNewShop: boolean
}

export interface ShopTemplate {
  code: string
  label: string
  description: string
  hasTables: boolean
  productCount: number
}

export const authApi = {
  templates: () => api.get<ShopTemplate[]>('/api/auth/templates'),
  requestOtp: (phone: string) =>
    api.post<{ phone: string; expiresInSeconds: number; devCode: string | null }>('/api/auth/otp', { phone }),
  verify: (phone: string, code: string, shopName?: string, templateCode?: string) =>
    api.post<AuthResult>('/api/auth/verify', { phone, code, shopName, templateCode }),
}

export interface StaffRow {
  id: string
  phone: string
  name: string
  role: 'Owner' | 'Staff' | 'SysAdmin'
  lastSeenAt: string | null
  createdAt: string
  isMe: boolean
}

export const staffApi = {
  list: () => api.get<StaffRow[]>('/api/staff'),
  save: (v: { id?: string; phone: string; name: string; role: string }) =>
    api.post<{ id: string }>('/api/staff', v),
  remove: (id: string) => api.del<void>(`/api/staff/${id}`),
}

// ───────────────────────── danh mục ─────────────────────────

interface CatalogResponse {
  shop: ShopSummary
  categories: Category[]
  products: Product[]
  zones: (Zone & { tables: DiningTable[] })[]
  expenseCategories: ExpenseCategory[]
  promotions: Promotion[]
  optionSets: OptionSet[]
  customers: CustomerRow[]
  serverTime: string
}

/**
 * Kéo toàn bộ danh mục về máy. Gọi một lần lúc mở app là chạy được cả ngày dù mất mạng.
 * Ghi đè hẳn thay vì trộn: máy chủ là nguồn sự thật cho danh mục, còn máy khách
 * là nguồn sự thật cho đơn hàng của nó.
 */
export async function pullCatalog(): Promise<ShopSummary> {
  const data = await api.get<CatalogResponse>('/api/catalog')

  const tables: DiningTable[] = data.zones.flatMap((z) =>
    z.tables.map((t) => ({ ...t, zoneId: z.id })),
  )
  const zones: Zone[] = data.zones.map(({ id, name, sortOrder }) => ({ id, name, sortOrder }))

  await db.transaction(
    'rw',
    [db.categories, db.products, db.zones, db.diningTables, db.expenseCategories, db.promotions, db.optionSets, db.customers, db.kv],
    async () => {
      await Promise.all([
        db.categories.clear(),
        db.products.clear(),
        db.zones.clear(),
        db.diningTables.clear(),
        db.expenseCategories.clear(),
        db.promotions.clear(),
        db.optionSets.clear(),
        db.customers.clear(),
      ])
      await Promise.all([
        db.categories.bulkAdd(data.categories),
        db.products.bulkAdd(data.products),
        db.zones.bulkAdd(zones),
        db.diningTables.bulkAdd(tables),
        db.expenseCategories.bulkAdd(data.expenseCategories),
        // Máy chủ cũ chưa trả ô này thì coi như quán chưa có chương trình nào.
        db.promotions.bulkAdd(data.promotions ?? []),
        // Máy chủ cũ chưa trả ô này thì quán chưa có bộ ghi chú nào — không phải lỗi.
        db.optionSets.bulkAdd(data.optionSets ?? []),
        // Danh sách khách quen đi cùng danh mục, để máy bán tra được lúc mất mạng. Số dư
        // không kéo theo — nó tính lại từ đơn và tiền trả ngay trên máy này.
        db.customers.bulkAdd(
          (data.customers ?? []).map(({ id, name, phone, note, debtLimit }) => ({
            id,
            name,
            phone,
            note,
            debtLimit,
          })),
        ),
      ])
      await kvSet('shop', data.shop)
      await kvSet('catalogPulledAt', new Date().toISOString())
    },
  )

  return data.shop
}

export const cachedShop = () => kvGet<ShopSummary>('shop')

/**
 * Sửa danh mục là việc <b>cần có mạng</b>, khác với bán hàng.
 * Máy chủ là nguồn sự thật cho danh mục — `pullCatalog` xoá sạch rồi ghi đè, nên
 * một món thêm lúc offline sẽ bị cuốn mất ở lần kéo danh mục kế tiếp. Thà báo thẳng
 * "cần có mạng" còn hơn để chủ quán nhập 20 món rồi thấy nó biến mất.
 */
export const catalogApi = {
  saveProduct: (p: {
    id?: string
    categoryId: string | null
    name: string
    price: number
    unit: string
    quickOptions: string | null
    soldByWeight: boolean
    barcode: string | null
    imageKey: string | null
    optionSetIds: string[]
  }) => api.post<{ id: string }>('/api/catalog/products', p),

  saveOptionSet: (o: { id?: string; name: string; choices: string[] }) =>
    api.post<{ id: string; name: string; choices: string[] }>('/api/catalog/option-sets', o),

  deleteOptionSet: (id: string) => api.del<void>(`/api/catalog/option-sets/${id}`),

  /** Ảnh cho món hiếm không có trong kho dựng sẵn. Máy chủ thu nhỏ rồi trả về mã. */
  uploadProductImage: (imageBase64: string) =>
    api.post<{ key: string }>('/api/catalog/product-image', { imageBase64 }),

  deleteProduct: (id: string) => api.del<void>(`/api/catalog/products/${id}`),

  saveCategory: (c: { id?: string; name: string; sortOrder?: number }) =>
    api.post<{ id: string; name: string; sortOrder: number }>('/api/catalog/categories', c),

  /**
   * moveTo bỏ trống nghĩa là đẩy món trong nhóm về "chưa xếp nhóm".
   * Máy chủ từ chối xoá nhóm còn món nếu không nói rõ chúng đi đâu.
   */
  deleteCategory: (id: string, moveTo?: string) =>
    api.del<{ moved: number }>(
      `/api/catalog/categories/${id}${moveTo ? `?moveTo=${moveTo}` : ''}`,
    ),

  reorderCategories: (ids: string[]) =>
    api.put<{ saved: number }>('/api/catalog/categories/order', { ids }),

  saveShop: (v: {
    name: string
    address: string | null
    phone: string | null
    taxCode: string | null
    businessType: 'FnB' | 'Retail'
    serviceChargePermille: number
    payLater: boolean
    /** 6 chữ số là đặt mã mới, chuỗi rỗng là bỏ mã, không truyền là giữ nguyên. */
    voidPasscode?: string
  }) =>
    api.post<{
      name: string
      address: string | null
      phone: string
      taxCode: string | null
      businessType: 'FnB' | 'Retail'
      serviceChargePermille: number
      payLater: boolean
      vatRatePermille: number
      pitRatePermille: number
      voidPasscodeHash: string | null
    }>('/api/catalog/shop', v),

  saveExpenseCategory: (c: { id?: string; name: string; emoji: string }) =>
    api.post<{ id: string; name: string; emoji: string }>('/api/catalog/expense-categories', c),

  deleteExpenseCategory: (id: string) =>
    api.del<void>(`/api/catalog/expense-categories/${id}`),

  reorderExpenseCategories: (ids: string[]) =>
    api.put<{ saved: number }>('/api/catalog/expense-categories/order', { ids }),

  findBarcode: (code: string) =>
    api.get<Product>(`/api/catalog/barcode/${encodeURIComponent(code)}`),

  savePromotion: (p: {
    id?: string
    name: string
    kind: 'Percent' | 'Amount'
    value: number
    minOrderTotal: number
    maxDiscount: number
    startsOn: string | null
    endsOn: string | null
  }) => api.post<{ id: string }>('/api/catalog/promotions', p),

  deletePromotion: (id: string) => api.del<void>(`/api/catalog/promotions/${id}`),
}

// ───────────────────────── sơ đồ bàn ─────────────────────────

export interface TableLayoutItem {
  id: string
  gridX: number
  gridY: number
  gridW: number
  gridH: number
}

/** Khách quen được ghi sổ. `debt` chỉ có ý nghĩa ở mấy endpoint tra cứu. */
export interface CustomerRow {
  id: string
  name: string
  phone: string | null
  note: string | null
  debtLimit: number
  debt: number
  lastOrderAt: string | null
}

export const customerApi = {
  list: () => api.get<CustomerRow[]>('/api/customers'),

  save: (c: { id?: string; name: string; phone: string | null; note: string | null; debtLimit: number }) =>
    api.post<CustomerRow>('/api/customers', c),

  remove: (id: string) => api.del<{ id: string }>(`/api/customers/${id}`),
}

export const tableApi = {
  options: () => api.get<{ palette: string[]; icons: string[] }>('/api/tables/options'),

  save: (t: {
    id?: string
    zoneId: string
    name: string
    icon: string | null
    color: string | null
    gridX: number
    gridY: number
    gridW: number
    gridH: number
    seats: number
    isTakeaway: boolean
  }) => api.post<{ id: string }>('/api/tables', t),

  saveLayout: (tables: TableLayoutItem[]) =>
    api.put<{ saved: number }>('/api/tables/layout', { tables }),

  setMode: (mode: TableLayoutMode, gridColumns: number, tileScale?: number) =>
    api.post<{ mode: TableLayoutMode; gridColumns: number; tileScale: number }>(
      '/api/tables/mode',
      { mode, gridColumns, tileScale },
    ),

  reserve: (
    id: string,
    body: { reservedAt: string | null; name?: string; phone?: string; partySize?: number; note?: string },
  ) => api.post<unknown>(`/api/tables/${id}/reserve`, body),

  remove: (id: string) => api.del<void>(`/api/tables/${id}`),

  saveZone: (z: { id?: string; name: string }) =>
    api.post<{ id: string; name: string }>('/api/tables/zones', z),

  /** moveTo bỏ trống thì máy chủ từ chối xoá khu vực còn bàn. */
  deleteZone: (id: string, moveTo?: string) =>
    api.del<{ moved: number }>(`/api/tables/zones/${id}${moveTo ? `?moveTo=${moveTo}` : ''}`),

  reorderZones: (ids: string[]) => api.put<{ saved: number }>('/api/tables/zones/order', { ids }),
}

export interface OrderRow {
  id: string
  code: string
  tableName: string | null
  status: string
  paidAt: string | null
  total: number
  paymentMethod: string | null
  itemCount: number
  voidedAt: string | null
  deviceId: string | null
}

export interface OrderLineDto {
  name: string
  optionLabel: string | null
  qtyMilli: number
  unitPrice: number
  lineTotal: number
  note: string | null
}

export interface OrderDetail {
  id: string
  code: string
  tableName: string | null
  status: string
  openedAt: string
  paidAt: string | null
  subtotal: number
  discount: number
  total: number
  paymentMethod: string | null
  amountGiven: number
  changeGiven: number
  customerName: string | null
  note: string | null
  deviceId: string | null
  voidedAt: string | null
  voidReason: string | null
  lines: OrderLineDto[]
  discountLabel: string | null
  serviceFee: number
  serviceFeePermille: number
  guests: number
}

export const ordersApi = {
  /** date theo dạng YYYY-MM-DD, tính theo ngày giờ Việt Nam. */
  list: (date: string, q?: string) => {
    const p = new URLSearchParams({ date })
    if (q) p.set('q', q)
    return api.get<OrderRow[]>(`/api/orders?${p}`)
  },

  get: (id: string) => api.get<OrderDetail>(`/api/orders/${id}`),

  void: (id: string, reason: string) =>
    api.post<{ id: string }>(`/api/orders/${id}/void`, { reason }),
}

export interface MenuDraft {
  name: string
  price: number
  unit: string
  category: string | null
  quickOptions: string | null
}

export const menuApi = {
  /** Trả về BẢN NHÁP — chưa ghi gì vào cơ sở dữ liệu, chủ quán xem lại rồi mới lưu. */
  scan: (imageBase64: string) =>
    api.post<{ products: MenuDraft[]; note: string | null; scansLeftToday: number }>(
      '/api/catalog/scan-menu',
      { imageBase64 },
    ),

  bulkCreate: (products: MenuDraft[]) =>
    api.post<{ created: number; skipped: number; skippedNames: string[] }>(
      '/api/catalog/products/bulk',
      { products },
    ),
}

export interface PlanStatus {
  plan: string
  expiresAt: string | null
  daysLeft: number
  active: boolean
  paymentReady: boolean
  packages: { months: number; amount: number; label: string }[]
}

export interface SubscriptionInvoice {
  id: string
  code: string
  amount: number
  months: number
  status: 'Pending' | 'Paid' | 'Cancelled'
  expiresAt: string
  qrUrl: string | null
  bankCode: string | null
  accountNumber: string | null
  accountName: string | null
}

export const billingApi = {
  status: () => api.get<PlanStatus>('/api/billing/status'),
  createInvoice: (months: number) =>
    api.post<SubscriptionInvoice>('/api/billing/invoice', { months }),
  getInvoice: (id: string) => api.get<SubscriptionInvoice>(`/api/billing/invoice/${id}`),
}

// ───────────────────────── báo cáo ─────────────────────────

export interface QuarterReport {
  year: number
  quarter: number
  revenue: number
  months: { month: number; revenue: number }[]
  vatRatePermille: number
  pitRatePermille: number
  vatAmount: number
  pitAmount: number
  taxTotal: number
  yearRevenue: number
  taxFreeThreshold: number
  overThreshold: boolean
  dueDate: string
}

export interface SummaryPoint {
  label: string
  revenue: number
  orderCount: number
}

export interface NamedAmount {
  name: string
  revenue: number
  orderCount: number
}

export type ReportPeriod = 'today' | '7d' | '30d' | '12m'

export interface SummaryReport {
  period: ReportPeriod
  from: string
  to: string
  revenue: number
  expense: number
  profit: number
  orderCount: number
  avgOrder: number
  /** Kỳ liền trước, dài đúng bằng kỳ này. Dùng để so hơn kém. */
  prevRevenue: number
  prevOrderCount: number
  prevAvgOrder: number
  points: SummaryPoint[]
  categories: NamedAmount[]
  kinds: NamedAmount[]
  payments: NamedAmount[]
  top: { name: string; qtyMilli: number; revenue: number }[]
}

export const reportApi = {
  /** Cả bảng điều khiển trong một lần gọi — máy quầy ở chợ không chờ nổi bảy lượt. */
  summary: (period: ReportPeriod) =>
    api.get<SummaryReport>(`/api/reports/summary?period=${period}`),

  quarter: (year?: number, quarter?: number) => {
    const q = new URLSearchParams()
    if (year) q.set('year', String(year))
    if (quarter) q.set('quarter', String(quarter))
    return api.get<QuarterReport>(`/api/reports/quarter?${q}`)
  },
  ledger: (year?: number, quarter?: number) => {
    const q = new URLSearchParams()
    if (year) q.set('year', String(year))
    if (quarter) q.set('quarter', String(quarter))
    return api.get<{
      year: number
      quarter: number
      total: number
      rows: { no: number; date: string; code: string; description: string; amount: number }[]
    }>(`/api/reports/ledger?${q}`)
  },
}
